import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IMPERSONATION_HANDSHAKE_QUERY, IMPERSONATION_MESSAGE, useAuth } from './AuthContext';
import { getApiBaseUrl } from './utils/api';
import Icon from './Icon';
import './UserManagement.css';

const PRIVILEGE_TEMPLATE = {
  dashboard: ['view'],
  assets: ['view', 'create', 'update', 'delete'],
  software: ['view', 'create', 'update', 'delete'],
  tickets: ['view', 'create', 'update', 'delete'],
  selfservice: ['view', 'create'],
  analytics: ['view'],
  documents: ['view', 'create', 'delete'],
  orders: ['view', 'create', 'accept'],
  cisbenchmarks: ['view', 'update'],
  utilization: ['view'],
  settings: ['view', 'update'],
};

const EMPTY_HARDWARE_FORM = {
  profile: '',
  vendor: '',
  model: '',
  type: '',
  description: '',
  allTenants: false,
  tenants: [],
  excludedtenants: []
};

function ensurePrivilegesObject(input) {
  const source = input && typeof input === 'object' && input.privileges && typeof input.privileges === 'object'
    ? input.privileges
    : {};
  const normalized = {};
  Object.entries(PRIVILEGE_TEMPLATE).forEach(([section, actions]) => {
    normalized[section] = {};
    actions.forEach((action) => {
      normalized[section][action] = Boolean(source?.[section]?.[action]);
    });
  });
  return { privileges: normalized };
}

function parseTenantList(value) {
  if (!value || typeof value !== 'string') return [];
  const seen = new Set();
  return value
    .split(/[,;\n\r]+/)
    .map((entry) => entry.trim())
    .filter(Boolean)
    .filter((entry) => {
      const key = entry.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function tenantListIncludes(list, tenantName) {
  const target = String(tenantName || '').trim().toLowerCase();
  if (!target) return false;
  return (Array.isArray(list) ? list : []).some(
    (entry) => String(entry || '').trim().toLowerCase() === target
  );
}

function hardwareFormFromProfile(profile) {
  if (!profile) return { ...EMPTY_HARDWARE_FORM, tenants: [], excludedtenants: [] };
  const tenants = parseTenantList(profile.tenants);
  const allTenants = tenants.some((t) => t === '*' || t.toLowerCase() === 'all');
  return {
    profile: profile.profile || '',
    vendor: profile.vendor || '',
    model: profile.model || '',
    type: profile.type || '',
    description: profile.description || '',
    allTenants,
    tenants: allTenants ? [] : tenants,
    excludedtenants: parseTenantList(profile.excludedtenants)
  };
}

function sourceFieldKey(type, displayname) {
  return `${String(type || '').trim().toLowerCase()}::${String(displayname || '').trim().toLowerCase()}`;
}

function templateItemKey(item) {
  return [
    String(item?.maincaption || '').trim().toLowerCase(),
    String(item?.widgetgroupname || '').trim().toLowerCase(),
    String(item?.displayname || '').trim().toLowerCase(),
    String(item?.fieldtype || 'checkbox').trim().toLowerCase()
  ].join('::');
}

const ONBOARDING_FIELD_TYPE_OPTIONS = [
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'date', label: 'Date' },
  { value: 'yesno', label: 'Yes/No dropdown' },
  { value: 'textbox', label: 'Textbox' },
  { value: 'text', label: 'Text' }
];

function defaultValueForFieldType(fieldtype) {
  if (fieldtype === 'checkbox') return 'unchecked';
  if (fieldtype === 'yesno') return 'no';
  return '';
}

function normalizeBuilderDefaultValue(fieldtype, value) {
  if (fieldtype === 'checkbox') {
    const raw = String(value ?? '').trim().toLowerCase();
    if (['1', 'true', 'yes', 'checked', 'on'].includes(raw)) return 'checked';
    return 'unchecked';
  }
  if (fieldtype === 'yesno') {
    return String(value ?? '').trim().toLowerCase() === 'yes' ? 'yes' : 'no';
  }
  return value == null ? '' : String(value);
}

const STANDARD_ONBOARDING_TEMPLATE_FIELDS = [
  {
    maincaption: 'Personal Information',
    sectiondescription: '',
    widgetgroupname: '',
    displayname: 'Name',
    fieldtype: 'text',
    description: 'Enter first name',
    mandatory: true
  },
  {
    maincaption: 'Personal Information',
    sectiondescription: '',
    widgetgroupname: '',
    displayname: 'Surname',
    fieldtype: 'text',
    description: 'Enter last name',
    mandatory: true
  },
  {
    maincaption: 'Personal Information',
    sectiondescription: '',
    widgetgroupname: '',
    displayname: 'Phone Number',
    fieldtype: 'text',
    description: 'Enter phone number',
    mandatory: true
  },
  {
    maincaption: 'Personal Information',
    sectiondescription: '',
    widgetgroupname: '',
    displayname: 'Manager',
    fieldtype: 'text',
    description: 'Start typing to search for manager',
    mandatory: true
  },
  {
    maincaption: 'Personal Information',
    sectiondescription: '',
    widgetgroupname: '',
    displayname: 'Employee Start Date',
    fieldtype: 'date',
    description: 'When will the employee start their first day?',
    mandatory: true
  },
  {
    maincaption: 'Personal Information',
    sectiondescription: '',
    widgetgroupname: '',
    displayname: 'Time-Limited Contract?',
    fieldtype: 'yesno',
    description: 'Specify if this onboarding is for a temporary/fixed-term contract.',
    mandatory: false
  },
  {
    maincaption: 'Additional Information',
    sectiondescription: '',
    widgetgroupname: '',
    displayname: 'Additional Information about New Employee',
    fieldtype: 'textbox',
    description: 'Add any relevant notes or special instructions',
    mandatory: false
  }
];

const UserManagement = () => {
  const { authenticatedFetch, user, selectedTenant, refreshPrivileges } = useAuth();
  const [activeSection, setActiveSection] = useState('users');
  const [users, setUsers] = useState([]);
  const [availableTenants, setAvailableTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyUserId, setBusyUserId] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [form, setForm] = useState({
    username: '',
    role: 'user',
    tenantAccessTenant: ''
  });
  const [createBusy, setCreateBusy] = useState(false);
  const [message, setMessage] = useState('');

  const [hardwareProfiles, setHardwareProfiles] = useState([]);
  const [hardwareLoading, setHardwareLoading] = useState(false);
  const [selectedHardwareId, setSelectedHardwareId] = useState(null);
  const [hardwareForm, setHardwareForm] = useState(() => ({
    ...EMPTY_HARDWARE_FORM,
    tenants: [],
    excludedtenants: []
  }));
  const [hardwareBusy, setHardwareBusy] = useState(false);
  const [isCreatingHardware, setIsCreatingHardware] = useState(false);

  const [templateTenant, setTemplateTenant] = useState('');
  const [templateSourcesByType, setTemplateSourcesByType] = useState({});
  const [templateItems, setTemplateItems] = useState([]);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateBusy, setTemplateBusy] = useState(false);
  const [activeSourceType, setActiveSourceType] = useState('');
  const [selectedSourceKeys, setSelectedSourceKeys] = useState(() => new Set());
  const [mainCaption, setMainCaption] = useState('Permissions & Access');
  const [sectionDescription, setSectionDescription] = useState('');
  const [customWidgetGroupName, setCustomWidgetGroupName] = useState('');
  const [customFieldName, setCustomFieldName] = useState('');
  const [fieldType, setFieldType] = useState('checkbox');
  const [fieldDescription, setFieldDescription] = useState('');
  const [fieldMandatory, setFieldMandatory] = useState(false);
  const [fieldDefaultValue, setFieldDefaultValue] = useState('unchecked');

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/admin/users`);
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        setError(data.error || 'Failed to load users');
        setUsers([]);
      } else {
        const rows = Array.isArray(data.users) ? data.users : [];
        setUsers(rows.map((row) => ({
          ...row,
          role: (row.role || 'user').toLowerCase(),
          privileges: ensurePrivilegesObject(row.privileges)
        })));
      }
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Network error while loading users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch]);

  const fetchAvailableTenants = useCallback(async () => {
    try {
      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/tenants`);
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !Array.isArray(data.tenants)) {
        setAvailableTenants([]);
        return;
      }
      setAvailableTenants(
        data.tenants
          .map((t) => (typeof t?.name === 'string' ? t.name.trim() : ''))
          .filter(Boolean)
      );
    } catch (err) {
      console.error('Error loading tenants for access editor:', err);
      setAvailableTenants([]);
    }
  }, [authenticatedFetch]);

  const fetchHardwareProfiles = useCallback(async () => {
    try {
      setHardwareLoading(true);
      setError('');
      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/admin/approved-hardware`);
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        setError(data.error || 'Failed to load approved hardware');
        setHardwareProfiles([]);
      } else {
        setHardwareProfiles(Array.isArray(data.profiles) ? data.profiles : []);
      }
    } catch (err) {
      console.error('Error loading approved hardware:', err);
      setError('Network error while loading hardware');
      setHardwareProfiles([]);
    } finally {
      setHardwareLoading(false);
    }
  }, [authenticatedFetch]);

  const fetchOnboardingTemplate = useCallback(async (tenantName) => {
    const tenant = String(tenantName || '').trim();
    if (!tenant) {
      setTemplateSourcesByType({});
      setTemplateItems([]);
      setActiveSourceType('');
      setSelectedSourceKeys(new Set());
      return;
    }
    try {
      setTemplateLoading(true);
      setError('');
      const response = await authenticatedFetch(
        `${getApiBaseUrl()}/api/admin/onboarding-templates?tenant=${encodeURIComponent(tenant)}`
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        setError(data.error || data.details || 'Failed to load onboarding template');
        setTemplateSourcesByType({});
        setTemplateItems([]);
        setActiveSourceType('');
        return;
      }
      const byType = data.sourcesByType && typeof data.sourcesByType === 'object'
        ? data.sourcesByType
        : {};
      setTemplateSourcesByType(byType);
      setTemplateItems(
        Array.isArray(data.template)
          ? data.template.map((row) => ({
              maincaption: row.maincaption || 'Permissions & Access',
              sectiondescription: row.sectiondescription || '',
              widgetgroupname: row.widgetgroupname || '',
              displayname: row.displayname || '',
              fieldtype: row.fieldtype || 'checkbox',
              description: row.description || '',
              mandatory: Boolean(row.mandatory),
              defaultvalue: normalizeBuilderDefaultValue(
                row.fieldtype || 'checkbox',
                row.defaultvalue
              )
            }))
          : []
      );
      const typeNames = Object.keys(byType).sort((a, b) => a.localeCompare(b));
      setActiveSourceType((prev) => (typeNames.includes(prev) ? prev : (typeNames[0] || '')));
      setSelectedSourceKeys(new Set());
      setMainCaption('Permissions & Access');
      setSectionDescription('');
      setCustomWidgetGroupName('');
      setCustomFieldName('');
      setFieldType('checkbox');
      setFieldDescription('');
      setFieldMandatory(false);
      setFieldDefaultValue('unchecked');
    } catch (err) {
      console.error('Error loading onboarding template:', err);
      setError('Network error while loading onboarding template');
      setTemplateSourcesByType({});
      setTemplateItems([]);
    } finally {
      setTemplateLoading(false);
    }
  }, [authenticatedFetch]);

  useEffect(() => {
    fetchUsers();
    fetchAvailableTenants();
  }, [fetchUsers, fetchAvailableTenants]);

  useEffect(() => {
    if (activeSection === 'hardware') {
      fetchHardwareProfiles();
    }
  }, [activeSection, fetchHardwareProfiles]);

  useEffect(() => {
    if (!templateTenant && availableTenants.length > 0) {
      const preferred = selectedTenant?.name && availableTenants.includes(selectedTenant.name)
        ? selectedTenant.name
        : availableTenants[0];
      setTemplateTenant(preferred);
    }
  }, [availableTenants, selectedTenant, templateTenant]);

  useEffect(() => {
    if (activeSection === 'onboarding-templates' && templateTenant) {
      fetchOnboardingTemplate(templateTenant);
    }
  }, [activeSection, templateTenant, fetchOnboardingTemplate]);

  const selectedUser = useMemo(
    () => users.find((u) => String(u.id) === String(selectedUserId)) || null,
    [users, selectedUserId]
  );

  const selectedHardware = useMemo(
    () => hardwareProfiles.find((p) => String(p.id) === String(selectedHardwareId)) || null,
    [hardwareProfiles, selectedHardwareId]
  );

  const sourceTypeNames = useMemo(
    () => Object.keys(templateSourcesByType).sort((a, b) => a.localeCompare(b)),
    [templateSourcesByType]
  );

  const activeSourceFields = useMemo(
    () => (activeSourceType && Array.isArray(templateSourcesByType[activeSourceType])
      ? templateSourcesByType[activeSourceType]
      : []),
    [activeSourceType, templateSourcesByType]
  );

  const templateGroups = useMemo(() => {
    const sections = new Map();
    templateItems.forEach((item) => {
      const caption = String(item.maincaption || 'General').trim() || 'General';
      if (!sections.has(caption)) {
        sections.set(caption, {
          maincaption: caption,
          sectiondescription: item.sectiondescription || '',
          fields: []
        });
      }
      const section = sections.get(caption);
      if (!section.sectiondescription && item.sectiondescription) {
        section.sectiondescription = item.sectiondescription;
      }
      section.fields.push(item);
    });
    return Array.from(sections.values()).map((section) => {
      const checkboxGroupsMap = new Map();
      const otherFields = [];
      section.fields.forEach((field) => {
        if (String(field.fieldtype || '').toLowerCase() === 'checkbox') {
          const groupName = String(field.widgetgroupname || 'Options').trim() || 'Options';
          if (!checkboxGroupsMap.has(groupName)) checkboxGroupsMap.set(groupName, []);
          checkboxGroupsMap.get(groupName).push(field);
        } else {
          otherFields.push(field);
        }
      });
      return {
        ...section,
        otherFields,
        checkboxGroups: Array.from(checkboxGroupsMap.entries()).map(([name, fields]) => ({
          name,
          fields
        }))
      };
    });
  }, [templateItems]);

  const [activeTemplateCaption, setActiveTemplateCaption] = useState('');
  const [activeTemplateWidgetGroup, setActiveTemplateWidgetGroup] = useState('');
  const [dragSectionIndex, setDragSectionIndex] = useState(null);
  const [dragOverSectionIndex, setDragOverSectionIndex] = useState(null);

  const templateItemKeySet = useMemo(() => {
    const keys = new Set();
    templateItems.forEach((item) => {
      keys.add(templateItemKey(item));
    });
    return keys;
  }, [templateItems]);

  const reorderTemplateSections = (fromIndex, toIndex) => {
    if (
      !Number.isInteger(fromIndex) ||
      !Number.isInteger(toIndex) ||
      fromIndex === toIndex ||
      fromIndex < 0 ||
      toIndex < 0
    ) {
      return;
    }

    setTemplateItems((prev) => {
      const sectionOrder = [];
      const bySection = new Map();
      prev.forEach((item) => {
        const caption = String(item.maincaption || 'General').trim() || 'General';
        if (!bySection.has(caption)) {
          bySection.set(caption, []);
          sectionOrder.push(caption);
        }
        bySection.get(caption).push(item);
      });
      if (fromIndex >= sectionOrder.length || toIndex >= sectionOrder.length) {
        return prev;
      }
      const nextOrder = [...sectionOrder];
      const [moved] = nextOrder.splice(fromIndex, 1);
      nextOrder.splice(toIndex, 0, moved);
      const next = [];
      nextOrder.forEach((caption) => {
        (bySection.get(caption) || []).forEach((item) => next.push(item));
      });
      return next;
    });
    setMessage('Section order updated — save the template to keep it');
    clearMessageSoon();
  };

  const selectTemplateTarget = ({
    maincaption,
    sectiondescription,
    widgetgroupname,
    fieldtype
  } = {}) => {
    const caption = String(maincaption || '').trim();
    const widgetGroup = widgetgroupname == null ? null : String(widgetgroupname || '').trim();
    if (caption) {
      setMainCaption(caption);
      setActiveTemplateCaption(caption);
    }
    if (sectiondescription != null) {
      setSectionDescription(String(sectiondescription || '').trim());
    }
    if (widgetGroup != null) {
      setCustomWidgetGroupName(widgetGroup);
      setActiveTemplateWidgetGroup(widgetGroup);
      if (widgetGroup && sourceTypeNames.includes(widgetGroup)) {
        setActiveSourceType(widgetGroup);
        setSelectedSourceKeys(new Set());
      }
    } else {
      setActiveTemplateWidgetGroup('');
    }
    if (fieldtype) {
      setFieldType(fieldtype);
    } else if (widgetGroup) {
      setFieldType('checkbox');
    }
    setMessage(
      widgetGroup
        ? `Editing "${caption}" / "${widgetGroup}"`
        : `Editing section "${caption}"`
    );
    clearMessageSoon();
  };

  const clearMessageSoon = () => {
    setTimeout(() => setMessage(''), 2500);
  };

  const parseTenantAccess = (tenantAccessValue) => {
    const raw = typeof tenantAccessValue === 'string' ? tenantAccessValue.trim() : '';
    if (!raw) return { all: false, tenants: [] };
    if (raw.split(',').map((v) => v.trim()).includes('*')) {
      return { all: true, tenants: [] };
    }
    const seen = new Set();
    const tenants = raw
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean)
      .filter((t) => {
        const key = t.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    return { all: false, tenants };
  };

  const formatTenantAccess = (all, tenants) => {
    if (all) return '*';
    const seen = new Set();
    const deduped = (Array.isArray(tenants) ? tenants : [])
      .map((v) => String(v || '').trim())
      .filter(Boolean)
      .filter((t) => {
        const key = t.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    return deduped.join(',');
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!form.username.trim()) return;
    if (form.role === 'user' && !form.tenantAccessTenant) {
      setError('Select a tenant for the new user.');
      return;
    }

    setCreateBusy(true);
    setError('');
    try {
      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/admin/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: form.username.trim(),
          role: form.role,
          tenantAccess: form.role === 'admin' ? '*' : form.tenantAccessTenant
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        setError(data.error || 'Could not create user');
      } else {
        setMessage(`Created ${form.role} "${form.username.trim()}"`);
        clearMessageSoon();
        setForm({ username: '', role: 'user', tenantAccessTenant: '' });
        fetchUsers();
      }
    } catch (err) {
      console.error('Error creating user:', err);
      setError('Network error while creating user');
    } finally {
      setCreateBusy(false);
    }
  };

  const handleImpersonateUser = async (targetUser) => {
    if (!targetUser || user?.id === targetUser.id) return;
    setBusyUserId(targetUser.id);
    setError('');

    const origin = window.location.origin;
    const handshakeUrl = `${origin}${window.location.pathname || '/'}?${IMPERSONATION_HANDSHAKE_QUERY}`;

    const readyPromise = new Promise((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        window.removeEventListener('message', onMessage);
        reject(new Error('The impersonation window did not start in time.'));
      }, 15000);
      const onMessage = (event) => {
        if (event.origin !== origin) return;
        if (event.data?.type !== IMPERSONATION_MESSAGE.ready) return;
        window.clearTimeout(timeout);
        window.removeEventListener('message', onMessage);
        resolve(event.source);
      };
      window.addEventListener('message', onMessage);
    });

    const popup = window.open(handshakeUrl, '_blank');
    if (!popup) {
      setBusyUserId(null);
      setError('Pop-up blocked. Allow pop-ups for this site to impersonate a user.');
      return;
    }

    try {
      await readyPromise;
      const response = await authenticatedFetch(
        `${getApiBaseUrl()}/api/admin/users/${targetUser.id}/impersonate`,
        { method: 'POST' }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success || !data.token) {
        popup.postMessage({ type: IMPERSONATION_MESSAGE.failed }, origin);
        popup.close();
        setError(data.error || 'Could not impersonate user');
        return;
      }
      popup.postMessage(
        {
          type: IMPERSONATION_MESSAGE.tokens,
          token: data.token,
          refreshToken: data.refreshToken || ''
        },
        origin
      );
      const retry = window.setInterval(() => {
        if (popup.closed) {
          window.clearInterval(retry);
          return;
        }
        popup.postMessage(
          {
            type: IMPERSONATION_MESSAGE.tokens,
            token: data.token,
            refreshToken: data.refreshToken || ''
          },
          origin
        );
      }, 250);
      window.setTimeout(() => window.clearInterval(retry), 3000);
    } catch (err) {
      console.error('Error impersonating user:', err);
      try {
        popup.postMessage({ type: IMPERSONATION_MESSAGE.failed }, origin);
        popup.close();
      } catch (e) {}
      setError(err.message || 'Network error while starting impersonation');
    } finally {
      setBusyUserId(null);
    }
  };

  const handleDeleteUser = async (targetUser) => {
    if (!targetUser || !window.confirm(`Delete user "${targetUser.username}"?`)) return;
    setBusyUserId(targetUser.id);
    setError('');
    try {
      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/admin/users/${targetUser.id}`, {
        method: 'DELETE'
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        setError(data.error || 'Could not delete user');
      } else {
        setMessage(`Deleted "${targetUser.username}"`);
        clearMessageSoon();
        if (String(selectedUserId) === String(targetUser.id)) {
          setSelectedUserId(null);
        }
        fetchUsers();
      }
    } catch (err) {
      console.error('Error deleting user:', err);
      setError('Network error while deleting user');
    } finally {
      setBusyUserId(null);
    }
  };

  const togglePrivilege = (section, action, checked) => {
    setUsers((prev) => prev.map((u) => {
      if (String(u.id) !== String(selectedUserId)) return u;
      const nextPrivileges = ensurePrivilegesObject(u.privileges);
      nextPrivileges.privileges[section][action] = checked;
      return { ...u, privileges: nextPrivileges };
    }));
  };

  const handleRoleChange = (roleValue) => {
    setUsers((prev) => prev.map((u) => (
      String(u.id) === String(selectedUserId) ? { ...u, role: roleValue } : u
    )));
  };

  const handleSelectedUserTenantAccessAll = (checked) => {
    setUsers((prev) => prev.map((u) => (
      String(u.id) === String(selectedUserId)
        ? { ...u, tenantAccess: checked ? '*' : '' }
        : u
    )));
  };

  const toggleSelectedUserTenant = (tenantName, checked) => {
    setUsers((prev) => prev.map((u) => {
      if (String(u.id) !== String(selectedUserId)) return u;
      const parsed = parseTenantAccess(u.tenantAccess);
      if (parsed.all) {
        return { ...u, tenantAccess: checked ? '*' : '' };
      }
      const next = new Set(parsed.tenants);
      if (checked) next.add(tenantName);
      else next.delete(tenantName);
      return { ...u, tenantAccess: formatTenantAccess(false, Array.from(next)) };
    }));
  };

  const saveSelectedUserAccess = async () => {
    if (!selectedUser) return;
    setBusyUserId(selectedUser.id);
    setError('');
    try {
      const response = await authenticatedFetch(
        `${getApiBaseUrl()}/api/admin/users/${selectedUser.id}/privileges`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            role: selectedUser.role,
            tenantAccess: selectedUser.tenantAccess || '',
            privileges: ensurePrivilegesObject(selectedUser.privileges)
          })
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        setError(data.error || 'Could not save privileges');
      } else {
        setMessage(`Saved access for "${selectedUser.username}"`);
        clearMessageSoon();
        if (String(selectedUser.id) === String(user?.id)) {
          await refreshPrivileges();
        }
      }
    } catch (err) {
      console.error('Error saving privileges:', err);
      setError('Network error while saving privileges');
    } finally {
      setBusyUserId(null);
    }
  };

  const startCreateHardware = () => {
    setIsCreatingHardware(true);
    setSelectedHardwareId(null);
    setHardwareForm({
      ...EMPTY_HARDWARE_FORM,
      tenants: [],
      excludedtenants: []
    });
  };

  const selectHardwareProfile = (profile) => {
    setIsCreatingHardware(false);
    setSelectedHardwareId(profile.id);
    setHardwareForm(hardwareFormFromProfile(profile));
  };

  const updateHardwareField = (field, value) => {
    setHardwareForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggleHardwareTenant = (tenantName, checked) => {
    setHardwareForm((prev) => {
      if (prev.allTenants) return prev;
      const target = String(tenantName || '').trim().toLowerCase();
      const next = prev.tenants.filter(
        (entry) => String(entry || '').trim().toLowerCase() !== target
      );
      if (checked) next.push(tenantName);
      return { ...prev, tenants: next };
    });
  };

  const toggleHardwareExcludedTenant = (tenantName, checked) => {
    setHardwareForm((prev) => {
      const target = String(tenantName || '').trim().toLowerCase();
      const next = prev.excludedtenants.filter(
        (entry) => String(entry || '').trim().toLowerCase() !== target
      );
      if (checked) next.push(tenantName);
      return { ...prev, excludedtenants: next };
    });
  };

  const buildHardwarePayload = () => ({
    profile: hardwareForm.profile.trim(),
    vendor: hardwareForm.vendor.trim(),
    model: hardwareForm.model.trim(),
    type: hardwareForm.type.trim(),
    description: hardwareForm.description.trim(),
    tenants: hardwareForm.allTenants ? ['*'] : hardwareForm.tenants,
    excludedtenants: hardwareForm.excludedtenants
  });

  const saveHardwareProfile = async () => {
    if (!hardwareForm.profile.trim()) {
      setError('Profile is required');
      return;
    }
    if (!hardwareForm.allTenants && hardwareForm.tenants.length === 0) {
      setError('Select at least one tenant, or All tenants');
      return;
    }

    setHardwareBusy(true);
    setError('');
    try {
      const payload = buildHardwarePayload();
      const isEdit = !isCreatingHardware && selectedHardwareId != null;
      const url = isEdit
        ? `${getApiBaseUrl()}/api/admin/approved-hardware/${selectedHardwareId}`
        : `${getApiBaseUrl()}/api/admin/approved-hardware`;
      const response = await authenticatedFetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        setError(data.error || `Could not ${isEdit ? 'update' : 'create'} hardware profile`);
      } else {
        setMessage(isEdit ? `Updated "${payload.profile}"` : `Created "${payload.profile}"`);
        clearMessageSoon();
        setIsCreatingHardware(false);
        if (data.profile?.id != null) {
          setSelectedHardwareId(data.profile.id);
          setHardwareForm(hardwareFormFromProfile(data.profile));
        }
        await fetchHardwareProfiles();
      }
    } catch (err) {
      console.error('Error saving hardware profile:', err);
      setError('Network error while saving hardware');
    } finally {
      setHardwareBusy(false);
    }
  };

  const handleDeleteHardware = async (profile) => {
    if (!profile || !window.confirm(`Delete hardware profile "${profile.profile}"?`)) return;
    setHardwareBusy(true);
    setError('');
    try {
      const response = await authenticatedFetch(
        `${getApiBaseUrl()}/api/admin/approved-hardware/${profile.id}`,
        { method: 'DELETE' }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        setError(data.error || 'Could not delete hardware profile');
      } else {
        setMessage(`Deleted "${profile.profile}"`);
        clearMessageSoon();
        if (String(selectedHardwareId) === String(profile.id)) {
          setSelectedHardwareId(null);
          setIsCreatingHardware(false);
          setHardwareForm({
            ...EMPTY_HARDWARE_FORM,
            tenants: [],
            excludedtenants: []
          });
        }
        await fetchHardwareProfiles();
      }
    } catch (err) {
      console.error('Error deleting hardware profile:', err);
      setError('Network error while deleting hardware');
    } finally {
      setHardwareBusy(false);
    }
  };

  const toggleSourceField = (type, displayname, checked) => {
    const key = sourceFieldKey(type, displayname);
    setSelectedSourceKeys((prev) => {
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  const addSelectedSourcesToTemplate = () => {
    if (!activeSourceType || selectedSourceKeys.size === 0) {
      setError('Select one or more fields to add to the template');
      return;
    }
    const caption = (mainCaption || 'Permissions & Access').trim();
    const widgetGroup = (customWidgetGroupName || activeSourceType).trim();
    if (!caption) {
      setError('Main caption is required');
      return;
    }
    if (!widgetGroup) {
      setError('Widget group name is required for checkbox fields');
      return;
    }

    const fields = activeSourceFields.filter((field) =>
      selectedSourceKeys.has(sourceFieldKey(activeSourceType, field.displayname))
    );
    if (fields.length === 0) {
      setError('Select one or more fields to add to the template');
      return;
    }

    setError('');
    setTemplateItems((prev) => {
      const existing = new Set(prev.map((item) => templateItemKey(item)));
      const next = [...prev];
      fields.forEach((field) => {
        const displayname = String(field.displayname || '').trim();
        if (!displayname) return;
        const item = {
          maincaption: caption,
          sectiondescription: sectionDescription.trim(),
          widgetgroupname: widgetGroup,
          displayname,
          fieldtype: 'checkbox',
          description: String(field.description || '').trim(),
          mandatory: false,
          defaultvalue: 'unchecked'
        };
        const key = templateItemKey(item);
        if (existing.has(key)) return;
        existing.add(key);
        next.push(item);
      });
      return next;
    });
    setSelectedSourceKeys(new Set());
    setMessage(`Added ${fields.length} checkbox field(s) to "${caption}"`);
    clearMessageSoon();
  };

  const addCustomFieldToTemplate = () => {
    const caption = (mainCaption || 'General').trim();
    const displayname = customFieldName.trim();
    const selectedType = fieldType || 'text';
    const widgetGroup =
      selectedType === 'checkbox'
        ? (customWidgetGroupName || activeSourceType || 'Options').trim()
        : (customWidgetGroupName || '').trim();

    if (!caption) {
      setError('Main caption is required');
      return;
    }
    if (!displayname) {
      setError('Enter a custom field name');
      return;
    }
    if (selectedType === 'checkbox' && !widgetGroup) {
      setError('Widget group name is required for checkbox fields');
      return;
    }

    const item = {
      maincaption: caption,
      sectiondescription: sectionDescription.trim(),
      widgetgroupname: widgetGroup,
      displayname,
      fieldtype: selectedType,
      description: fieldDescription.trim(),
      mandatory: fieldMandatory,
      defaultvalue: normalizeBuilderDefaultValue(selectedType, fieldDefaultValue)
    };
    const key = templateItemKey(item);
    if (templateItemKeySet.has(key)) {
      setError(`"${displayname}" is already in "${caption}"`);
      return;
    }

    setError('');
    setTemplateItems((prev) => [...prev, item]);
    setCustomFieldName('');
    setFieldDescription('');
    setFieldMandatory(false);
    setFieldDefaultValue(defaultValueForFieldType(selectedType));
    setMessage(`Added ${selectedType} field "${displayname}" to "${caption}"`);
    clearMessageSoon();
  };

  const removeTemplateItem = (item) => {
    const key = templateItemKey(item);
    setTemplateItems((prev) => prev.filter((row) => templateItemKey(row) !== key));
  };

  const removeTemplateSection = (maincaption) => {
    const captionKey = String(maincaption || '').trim().toLowerCase();
    setTemplateItems((prev) =>
      prev.filter((item) => String(item.maincaption || '').trim().toLowerCase() !== captionKey)
    );
  };

  const addStandardFieldsToTemplate = () => {
    const toAdd = STANDARD_ONBOARDING_TEMPLATE_FIELDS.filter(
      (field) => !templateItemKeySet.has(templateItemKey(field))
    );
    if (toAdd.length === 0) {
      setError('');
      setMessage('Standard fields are already in the template');
      clearMessageSoon();
      return;
    }
    setTemplateItems((prev) => [...prev, ...toAdd.map((field) => ({ ...field }))]);
    setError('');
    setMessage(`Added ${toAdd.length} standard field${toAdd.length === 1 ? '' : 's'}`);
    clearMessageSoon();
  };

  const saveOnboardingTemplate = async () => {
    if (!templateTenant) {
      setError('Select a tenant first');
      return;
    }
    setTemplateBusy(true);
    setError('');
    setMessage('');
    try {
      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/admin/onboarding-templates`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenant: templateTenant,
          items: templateItems
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        setError(data.error || data.details || 'Failed to save onboarding template');
      } else {
        setTemplateItems(
          Array.isArray(data.template)
            ? data.template.map((row) => ({
                maincaption: row.maincaption || '',
                sectiondescription: row.sectiondescription || '',
                widgetgroupname: row.widgetgroupname || '',
                displayname: row.displayname || '',
                fieldtype: row.fieldtype || 'checkbox',
                description: row.description || '',
                mandatory: Boolean(row.mandatory),
                defaultvalue: normalizeBuilderDefaultValue(
                  row.fieldtype || 'checkbox',
                  row.defaultvalue
                )
              }))
            : []
        );
        setMessage('Onboarding template saved');
        clearMessageSoon();
      }
    } catch (err) {
      console.error('Error saving onboarding template:', err);
      setError('Network error while saving onboarding template');
    } finally {
      setTemplateBusy(false);
    }
  };

  const handleRefresh = () => {
    if (activeSection === 'hardware') {
      fetchHardwareProfiles();
    } else if (activeSection === 'onboarding-templates') {
      fetchOnboardingTemplate(templateTenant);
    } else {
      fetchUsers();
    }
  };

  const selectedTenantAccess = selectedUser ? parseTenantAccess(selectedUser.tenantAccess) : { all: false, tenants: [] };
  const showHardwareEditor = isCreatingHardware || selectedHardware != null;
  const refreshDisabled =
    activeSection === 'hardware'
      ? hardwareLoading
      : activeSection === 'onboarding-templates'
        ? templateLoading
        : loading;

  return (
    <div className="user-management-container">
      <div className="user-management-header">
        <div>
          <h2>Portal Management</h2>
          <p>Admin center for portal users, access, hardware, and onboarding templates.</p>
        </div>
        <button
          type="button"
          className="user-management-refresh"
          onClick={handleRefresh}
          disabled={refreshDisabled}
        >
          <Icon name="refresh" size="small" />
          Refresh
        </button>
      </div>

      <div className="user-management-tabs" role="tablist" aria-label="Portal management sections">
        <button
          type="button"
          role="tab"
          aria-selected={activeSection === 'users'}
          className={activeSection === 'users' ? 'active' : ''}
          onClick={() => setActiveSection('users')}
        >
          Users
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeSection === 'hardware'}
          className={activeSection === 'hardware' ? 'active' : ''}
          onClick={() => setActiveSection('hardware')}
        >
          Hardware
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeSection === 'onboarding-templates'}
          className={activeSection === 'onboarding-templates' ? 'active' : ''}
          onClick={() => setActiveSection('onboarding-templates')}
        >
          Onboarding templates
        </button>
      </div>

      {(error || message) && (
        <div className={`user-management-feedback ${error ? 'error' : 'success'}`}>
          {error || message}
        </div>
      )}

      {activeSection === 'users' ? (
        <>
          <form className="user-management-create" onSubmit={handleCreateUser}>
            <p className="user-management-template-note">
              New users inherit default privileges from the <strong>template</strong> portal user.
            </p>
            <input
              type="text"
              placeholder="Username"
              value={form.username}
              onChange={(e) => setForm((prev) => ({ ...prev, username: e.target.value }))}
              required
            />
            <select
              value={form.role}
              onChange={(e) => {
                const nextRole = e.target.value;
                setForm((prev) => ({
                  ...prev,
                  role: nextRole,
                  tenantAccessTenant: nextRole === 'admin' ? '' : prev.tenantAccessTenant
                }));
              }}
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
            {form.role === 'user' ? (
              <select
                value={form.tenantAccessTenant}
                onChange={(e) => setForm((prev) => ({ ...prev, tenantAccessTenant: e.target.value }))}
                required
              >
                <option value="">Select tenant access</option>
                {availableTenants.map((tenantName) => (
                  <option key={`create-tenant-${tenantName}`} value={tenantName}>
                    {tenantName}
                  </option>
                ))}
              </select>
            ) : (
              <div className="user-management-inline-note">Admin defaults to all tenants (`*`).</div>
            )}
            <button type="submit" disabled={createBusy}>
              {createBusy ? 'Adding…' : 'Add user'}
            </button>
          </form>

          {loading ? (
            <div className="user-management-state">Loading users...</div>
          ) : (
            <div className="user-management-grid">
              <div className="user-management-table-wrap">
                <table className="user-management-table">
                  <thead>
                    <tr>
                      <th>Username</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Tenant access</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((row) => (
                      <tr key={row.id} className={String(row.id) === String(selectedUserId) ? 'selected' : ''}>
                        <td>{row.username}</td>
                        <td>{row.role}</td>
                        <td>{row.status || 'active'}</td>
                        <td>{row.tenantAccess || '—'}</td>
                        <td className="user-management-row-actions">
                          <button type="button" onClick={() => setSelectedUserId(row.id)}>
                            Access
                          </button>
                          <button
                            type="button"
                            className="impersonate"
                            onClick={() => handleImpersonateUser(row)}
                            disabled={
                              busyUserId === row.id ||
                              user?.id === row.id ||
                              String(row.status || 'active').toLowerCase() !== 'active'
                            }
                            title={
                              user?.id === row.id
                                ? 'You are already signed in as this user'
                                : String(row.status || 'active').toLowerCase() !== 'active'
                                  ? 'Cannot impersonate an inactive user'
                                  : `Open a new window as ${row.username}`
                            }
                          >
                            {busyUserId === row.id ? 'Opening…' : 'Impersonate'}
                          </button>
                          <button
                            type="button"
                            className="danger"
                            onClick={() => handleDeleteUser(row)}
                            disabled={busyUserId === row.id || user?.id === row.id}
                            title={user?.id === row.id ? 'Cannot delete your own account' : 'Delete user'}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="user-management-access-panel">
                {!selectedUser ? (
                  <div className="user-management-state">Select a user to edit privileges.</div>
                ) : (
                  <>
                    <div className="access-panel-header">
                      <h3>{selectedUser.username}</h3>
                      <select
                        value={selectedUser.role}
                        onChange={(e) => handleRoleChange(e.target.value)}
                      >
                        <option value="user">user</option>
                        <option value="admin">admin</option>
                      </select>
                    </div>

                    <div className="access-panel-scroller">
                      <div className="access-section">
                        <h4>Tenant Access</h4>
                        <div className="access-toggles">
                          <label>
                            <input
                              type="checkbox"
                              checked={selectedTenantAccess.all}
                              onChange={(e) => handleSelectedUserTenantAccessAll(e.target.checked)}
                            />
                            All tenants (`*`)
                          </label>
                        </div>
                        {!selectedTenantAccess.all && (
                          <div className="access-toggles">
                            {availableTenants.map((tenantName) => (
                              <label key={`access-tenant-${tenantName}`}>
                                <input
                                  type="checkbox"
                                  checked={selectedTenantAccess.tenants.includes(tenantName)}
                                  onChange={(e) => toggleSelectedUserTenant(tenantName, e.target.checked)}
                                />
                                {tenantName}
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                      {Object.entries(PRIVILEGE_TEMPLATE).map(([section, actions]) => (
                        <div key={section} className="access-section">
                          <h4>{section}</h4>
                          <div className="access-toggles">
                            {actions.map((action) => (
                              <label key={`${section}-${action}`}>
                                <input
                                  type="checkbox"
                                  checked={Boolean(selectedUser.privileges?.privileges?.[section]?.[action])}
                                  onChange={(e) => togglePrivilege(section, action, e.target.checked)}
                                />
                                {action}
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>

                    <button
                      type="button"
                      className="save-access"
                      onClick={saveSelectedUserAccess}
                      disabled={busyUserId === selectedUser.id}
                    >
                      {busyUserId === selectedUser.id ? 'Saving…' : 'Save access'}
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </>
      ) : activeSection === 'hardware' ? (
        <>
          <div className="user-management-create hardware-create-bar">
            <div className="user-management-inline-note">
              Manage standard hardware profiles used by Self Service Order New Equipment.
            </div>
            <button type="button" onClick={startCreateHardware} disabled={hardwareBusy}>
              Add hardware
            </button>
          </div>

          {hardwareLoading ? (
            <div className="user-management-state">Loading hardware...</div>
          ) : (
            <div className="user-management-grid">
              <div className="user-management-table-wrap">
                <table className="user-management-table">
                  <thead>
                    <tr>
                      <th>Profile</th>
                      <th>Vendor</th>
                      <th>Model</th>
                      <th>Type</th>
                      <th>Tenants</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hardwareProfiles.length === 0 ? (
                      <tr>
                        <td colSpan={6}>No hardware profiles yet.</td>
                      </tr>
                    ) : (
                      hardwareProfiles.map((row) => (
                        <tr
                          key={row.id}
                          className={
                            !isCreatingHardware && String(row.id) === String(selectedHardwareId)
                              ? 'selected'
                              : ''
                          }
                        >
                          <td>{row.profile || '—'}</td>
                          <td>{row.vendor || '—'}</td>
                          <td>{row.model || '—'}</td>
                          <td>{row.type || '—'}</td>
                          <td>{row.tenants || '—'}</td>
                          <td className="user-management-row-actions">
                            <button type="button" onClick={() => selectHardwareProfile(row)}>
                              Edit
                            </button>
                            <button
                              type="button"
                              className="danger"
                              onClick={() => handleDeleteHardware(row)}
                              disabled={hardwareBusy}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="user-management-access-panel">
                {!showHardwareEditor ? (
                  <div className="user-management-state">
                    Select a hardware profile to edit, or click Add hardware.
                  </div>
                ) : (
                  <>
                    <div className="access-panel-header">
                      <h3>{isCreatingHardware ? 'New hardware' : hardwareForm.profile || 'Edit hardware'}</h3>
                    </div>

                    <div className="access-panel-scroller">
                      <div className="access-section">
                        <h4>Details</h4>
                        <div className="hardware-form-fields">
                          <label>
                            Profile *
                            <input
                              type="text"
                              value={hardwareForm.profile}
                              onChange={(e) => updateHardwareField('profile', e.target.value)}
                              required
                            />
                          </label>
                          <label>
                            Vendor
                            <input
                              type="text"
                              value={hardwareForm.vendor}
                              onChange={(e) => updateHardwareField('vendor', e.target.value)}
                            />
                          </label>
                          <label>
                            Model
                            <input
                              type="text"
                              value={hardwareForm.model}
                              onChange={(e) => updateHardwareField('model', e.target.value)}
                            />
                          </label>
                          <label>
                            Type
                            <input
                              type="text"
                              value={hardwareForm.type}
                              onChange={(e) => updateHardwareField('type', e.target.value)}
                            />
                          </label>
                          <label className="hardware-description-field">
                            Description
                            <textarea
                              rows={4}
                              value={hardwareForm.description}
                              onChange={(e) => updateHardwareField('description', e.target.value)}
                              placeholder="Selling points shown in Order New Equipment"
                            />
                          </label>
                        </div>
                      </div>

                      <div className="access-section">
                        <h4>Tenants</h4>
                        <div className="access-toggles">
                          <label>
                            <input
                              type="checkbox"
                              checked={hardwareForm.allTenants}
                              onChange={(e) =>
                                setHardwareForm((prev) => ({
                                  ...prev,
                                  allTenants: e.target.checked,
                                  tenants: e.target.checked ? [] : prev.tenants
                                }))
                              }
                            />
                            All tenants (`*`)
                          </label>
                        </div>
                        {!hardwareForm.allTenants && (
                          <div className="access-toggles">
                                {availableTenants.map((tenantName) => (
                              <label key={`hw-tenant-${tenantName}`}>
                                <input
                                  type="checkbox"
                                  checked={tenantListIncludes(hardwareForm.tenants, tenantName)}
                                  onChange={(e) => toggleHardwareTenant(tenantName, e.target.checked)}
                                />
                                {tenantName}
                              </label>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="access-section">
                        <h4>Excluded tenants</h4>
                        <div className="access-toggles">
                          {availableTenants.map((tenantName) => (
                            <label key={`hw-excluded-${tenantName}`}>
                              <input
                                type="checkbox"
                                checked={tenantListIncludes(hardwareForm.excludedtenants, tenantName)}
                                onChange={(e) =>
                                  toggleHardwareExcludedTenant(tenantName, e.target.checked)
                                }
                              />
                              {tenantName}
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="hardware-panel-actions">
                      <button
                        type="button"
                        className="save-access"
                        onClick={saveHardwareProfile}
                        disabled={hardwareBusy}
                      >
                        {hardwareBusy
                          ? 'Saving…'
                          : isCreatingHardware
                            ? 'Create hardware'
                            : 'Save hardware'}
                      </button>
                      {!isCreatingHardware && selectedHardware && (
                        <button
                          type="button"
                          className="danger hardware-delete-btn"
                          onClick={() => handleDeleteHardware(selectedHardware)}
                          disabled={hardwareBusy}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="user-management-create hardware-create-bar onboarding-template-bar">
            <label className="onboarding-template-tenant">
              Tenant
              <select
                value={templateTenant}
                onChange={(e) => setTemplateTenant(e.target.value)}
              >
                <option value="">Select tenant</option>
                {availableTenants.map((tenantName) => (
                  <option key={`template-tenant-${tenantName}`} value={tenantName}>
                    {tenantName}
                  </option>
                ))}
              </select>
            </label>
            <div className="user-management-inline-note">
              Pick available fields or add custom fields to widget groups shown on the onboarding form.
            </div>
            <div className="onboarding-template-actions">
              <button
                type="button"
                className="onboarding-std-fields-btn"
                onClick={addStandardFieldsToTemplate}
                disabled={templateBusy || !templateTenant || templateLoading}
              >
                Add standards
              </button>
              <button
                type="button"
                className="save-access"
                onClick={saveOnboardingTemplate}
                disabled={templateBusy || !templateTenant || templateLoading}
              >
                {templateBusy ? 'Saving…' : 'Save template'}
              </button>
            </div>
          </div>

          {!templateTenant ? (
            <div className="user-management-state">Select a tenant to edit its onboarding template.</div>
          ) : templateLoading ? (
            <div className="user-management-state">Loading onboarding template...</div>
          ) : (
            <div className="onboarding-template-grid">
              <div className="onboarding-template-panel">
                <div className="access-panel-header">
                  <h3>Available fields</h3>
                </div>

                <div className="onboarding-builder-fields">
                  <label>
                    Main caption
                    <input
                      type="text"
                      value={mainCaption}
                      onChange={(e) => setMainCaption(e.target.value)}
                      placeholder="e.g. Permissions & Access"
                    />
                  </label>
                  <label>
                    Section description
                    <input
                      type="text"
                      value={sectionDescription}
                      onChange={(e) => setSectionDescription(e.target.value)}
                      placeholder="Optional short description for this section"
                    />
                  </label>
                  <label>
                    Widget group name
                    <input
                      type="text"
                      value={customWidgetGroupName}
                      onChange={(e) => setCustomWidgetGroupName(e.target.value)}
                      placeholder={activeSourceType || 'e.g. SharePoint Sites'}
                    />
                  </label>
                </div>

                <div className="onboarding-builder-fields onboarding-custom-field-builder">
                  <label>
                    Custom field
                    <input
                      type="text"
                      value={customFieldName}
                      onChange={(e) => setCustomFieldName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addCustomFieldToTemplate();
                        }
                      }}
                      placeholder="e.g. VPN access"
                    />
                  </label>
                  <label>
                    Field type
                    <select
                      value={fieldType}
                      onChange={(e) => {
                        const nextType = e.target.value;
                        setFieldType(nextType);
                        setFieldDefaultValue(defaultValueForFieldType(nextType));
                      }}
                    >
                      {ONBOARDING_FIELD_TYPE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Field description
                    <input
                      type="text"
                      value={fieldDescription}
                      onChange={(e) => setFieldDescription(e.target.value)}
                      placeholder="Optional help text / placeholder"
                    />
                  </label>
                  <label>
                    Default value
                    {fieldType === 'checkbox' ? (
                      <select
                        value={fieldDefaultValue === 'checked' ? 'checked' : 'unchecked'}
                        onChange={(e) => setFieldDefaultValue(e.target.value)}
                      >
                        <option value="unchecked">Unchecked</option>
                        <option value="checked">Checked</option>
                      </select>
                    ) : fieldType === 'yesno' ? (
                      <select
                        value={fieldDefaultValue === 'yes' ? 'yes' : 'no'}
                        onChange={(e) => setFieldDefaultValue(e.target.value)}
                      >
                        <option value="no">No</option>
                        <option value="yes">Yes</option>
                      </select>
                    ) : fieldType === 'date' ? (
                      <input
                        type="date"
                        value={fieldDefaultValue}
                        onChange={(e) => setFieldDefaultValue(e.target.value)}
                      />
                    ) : fieldType === 'textbox' ? (
                      <textarea
                        value={fieldDefaultValue}
                        onChange={(e) => setFieldDefaultValue(e.target.value)}
                        rows={2}
                        placeholder="Optional default text"
                      />
                    ) : (
                      <input
                        type="text"
                        value={fieldDefaultValue}
                        onChange={(e) => setFieldDefaultValue(e.target.value)}
                        placeholder="Optional default text"
                      />
                    )}
                  </label>
                  <label className="onboarding-mandatory-toggle">
                    <input
                      type="checkbox"
                      checked={fieldMandatory}
                      onChange={(e) => setFieldMandatory(e.target.checked)}
                    />
                    Mandatory field
                  </label>
                  <button
                    type="button"
                    className="onboarding-add-custom-btn"
                    onClick={addCustomFieldToTemplate}
                    disabled={!customFieldName.trim()}
                  >
                    Add custom field
                  </button>
                </div>

                <div className="onboarding-source-actions">
                  <div className="user-management-inline-note">
                    Available fields are always added as checkboxes under the main caption / widget group.
                  </div>
                  <button
                    type="button"
                    onClick={addSelectedSourcesToTemplate}
                    disabled={selectedSourceKeys.size === 0}
                  >
                    Add selected ({selectedSourceKeys.size})
                  </button>
                </div>

                {sourceTypeNames.length === 0 ? (
                  <div className="user-management-state">
                    No available fields for this tenant. Use main caption + custom field above.
                  </div>
                ) : (
                  <>
                    <div className="onboarding-type-tabs" role="tablist" aria-label="Form source types">
                      {sourceTypeNames.map((typeName) => (
                        <button
                          key={`source-type-${typeName}`}
                          type="button"
                          role="tab"
                          aria-selected={activeSourceType === typeName}
                          className={activeSourceType === typeName ? 'active' : ''}
                          onClick={() => {
                            setActiveSourceType(typeName);
                            setSelectedSourceKeys(new Set());
                            setCustomWidgetGroupName(typeName);
                          }}
                        >
                          {typeName}
                          <span className="onboarding-type-count">
                            {(templateSourcesByType[typeName] || []).length}
                          </span>
                        </button>
                      ))}
                    </div>

                    <div className="access-panel-scroller onboarding-source-list">
                      {activeSourceFields.length === 0 ? (
                        <div className="user-management-state">No fields in this category.</div>
                      ) : (
                        activeSourceFields.map((field) => {
                          const key = sourceFieldKey(activeSourceType, field.displayname);
                          const previewItem = {
                            maincaption: mainCaption || 'Permissions & Access',
                            widgetgroupname: customWidgetGroupName || activeSourceType,
                            displayname: field.displayname,
                            fieldtype: 'checkbox'
                          };
                          const alreadyAdded = templateItemKeySet.has(templateItemKey(previewItem));
                          return (
                            <label key={`source-${field.id || key}`} className="onboarding-source-item">
                              <input
                                type="checkbox"
                                checked={selectedSourceKeys.has(key)}
                                onChange={(e) =>
                                  toggleSourceField(activeSourceType, field.displayname, e.target.checked)
                                }
                              />
                              <span>
                                <strong>{field.displayname || '(unnamed)'}</strong>
                                {field.emailURL ? <small>{field.emailURL}</small> : null}
                                {alreadyAdded ? <em>Already in template</em> : null}
                              </span>
                            </label>
                          );
                        })
                      )}
                    </div>
                  </>
                )}
              </div>

              <div className="onboarding-template-panel">
                <div className="access-panel-header">
                  <h3>Template sections</h3>
                  <span className="user-management-inline-note">
                    {templateItems.length} field{templateItems.length === 1 ? '' : 's'}
                  </span>
                </div>
                <div className="user-management-inline-note" style={{ marginBottom: '0.35rem' }}>
                  Drag sections to reorder. Click a section or widget group to pre-fill the builder on the left.
                </div>
                {templateGroups.length === 0 ? (
                  <div className="user-management-state">
                    No fields in the template yet. Select fields on the left and add them.
                  </div>
                ) : (
                  <div className="access-panel-scroller">
                    {templateGroups.map((section, sectionIndex) => {
                      const sectionActive =
                        String(activeTemplateCaption || '').toLowerCase() ===
                        String(section.maincaption || '').toLowerCase();
                      const isDragging = dragSectionIndex === sectionIndex;
                      const isDragOver =
                        dragOverSectionIndex === sectionIndex &&
                        dragSectionIndex !== null &&
                        dragSectionIndex !== sectionIndex;
                      return (
                        <div
                          key={`template-section-${section.maincaption}`}
                          className={[
                            'access-section',
                            'onboarding-template-section',
                            sectionActive ? 'is-active' : '',
                            isDragging ? 'is-dragging' : '',
                            isDragOver ? 'is-drag-over' : ''
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          draggable
                          onDragStart={(e) => {
                            setDragSectionIndex(sectionIndex);
                            e.dataTransfer.effectAllowed = 'move';
                            e.dataTransfer.setData('text/plain', String(sectionIndex));
                          }}
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'move';
                            if (dragOverSectionIndex !== sectionIndex) {
                              setDragOverSectionIndex(sectionIndex);
                            }
                          }}
                          onDragLeave={() => {
                            if (dragOverSectionIndex === sectionIndex) {
                              setDragOverSectionIndex(null);
                            }
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            const fromIndex = Number(
                              e.dataTransfer.getData('text/plain') || dragSectionIndex
                            );
                            reorderTemplateSections(fromIndex, sectionIndex);
                            setDragSectionIndex(null);
                            setDragOverSectionIndex(null);
                          }}
                          onDragEnd={() => {
                            setDragSectionIndex(null);
                            setDragOverSectionIndex(null);
                          }}
                        >
                          <div className="onboarding-group-header">
                            <span
                              className="onboarding-section-drag-handle"
                              title="Drag to reorder"
                              aria-hidden="true"
                            >
                              ⋮⋮
                            </span>
                            <button
                              type="button"
                              className="onboarding-section-select"
                              onClick={() =>
                                selectTemplateTarget({
                                  maincaption: section.maincaption,
                                  sectiondescription: section.sectiondescription,
                                  widgetgroupname: ''
                                })
                              }
                            >
                              <h4>{section.maincaption}</h4>
                              {section.sectiondescription ? (
                                <small>{section.sectiondescription}</small>
                              ) : (
                                <small>Click to edit this section</small>
                              )}
                            </button>
                            <button
                              type="button"
                              className="danger hardware-delete-btn"
                              onClick={() => removeTemplateSection(section.maincaption)}
                            >
                              Remove section
                            </button>
                          </div>

                          {section.checkboxGroups.map((group) => {
                            const groupActive =
                              sectionActive &&
                              String(activeTemplateWidgetGroup || '').toLowerCase() ===
                                String(group.name || '').toLowerCase();
                            return (
                              <div
                                key={`template-group-${section.maincaption}-${group.name}`}
                                className={`onboarding-widget-group ${groupActive ? 'is-active' : ''}`}
                              >
                                <button
                                  type="button"
                                  className="onboarding-widget-group-select"
                                  onClick={() =>
                                    selectTemplateTarget({
                                      maincaption: section.maincaption,
                                      sectiondescription: section.sectiondescription,
                                      widgetgroupname: group.name,
                                      fieldtype: 'checkbox'
                                    })
                                  }
                                >
                                  <strong>{group.name}</strong>
                                  <small>
                                    {group.fields.length} checkbox
                                    {group.fields.length === 1 ? '' : 'es'} · click to add more
                                  </small>
                                </button>
                                <div className="onboarding-template-options">
                                  {group.fields.map((field) => (
                                    <div
                                      key={templateItemKey(field)}
                                      className="onboarding-template-option"
                                    >
                                      <span>
                                        <strong>{field.displayname}</strong>
                                        <small>
                                          {[
                                            field.mandatory ? 'mandatory' : null,
                                            field.defaultvalue === 'checked'
                                              ? 'default checked'
                                              : 'default unchecked'
                                          ]
                                            .filter(Boolean)
                                            .join(' · ')}
                                        </small>
                                        {field.description ? <small>{field.description}</small> : null}
                                      </span>
                                      <button type="button" onClick={() => removeTemplateItem(field)}>
                                        Remove
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })}

                          {section.otherFields.length > 0 && (
                            <div className="onboarding-template-options">
                              {section.otherFields.map((field) => (
                                <div
                                  key={templateItemKey(field)}
                                  className="onboarding-template-option"
                                >
                                  <button
                                    type="button"
                                    className="onboarding-field-select"
                                    onClick={() =>
                                      selectTemplateTarget({
                                        maincaption: section.maincaption,
                                        sectiondescription: section.sectiondescription,
                                        widgetgroupname: field.widgetgroupname || '',
                                        fieldtype: field.fieldtype || 'text'
                                      })
                                    }
                                  >
                                    <strong>{field.displayname}</strong>
                                    <small>
                                      {[
                                        field.fieldtype,
                                        field.widgetgroupname || null,
                                        field.mandatory ? 'mandatory' : null,
                                        field.defaultvalue
                                          ? `default: ${field.defaultvalue}`
                                          : null
                                      ]
                                        .filter(Boolean)
                                        .join(' · ')}
                                    </small>
                                    {field.description ? <small>{field.description}</small> : null}
                                  </button>
                                  <button type="button" onClick={() => removeTemplateItem(field)}>
                                    Remove
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UserManagement;
